import { useState, useEffect, useContext } from "react";
import { useNavigate } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { updateWellnessHelpful } from "@/api/wellness.api";
import { MyStoryContext } from "@/context/myStory.context";
import { useBootstrap, seedUserCache } from "@/features/bootstrap";

export function useCongratulations({ wellnessId, achievements }) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { data: user } = useBootstrap();
  const { fetchAllData } = useContext(MyStoryContext);
  const [showPopup, setShowPopup] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (!achievements?.user || !user?._id) {
      return;
    }
    const {
      coins,
      count,
      curDayStrek,
      curWeekStreak,
      maxDayStreak,
      maxWeekStreak,
      badges,
    } = achievements.user;
    seedUserCache(queryClient, user._id.toString(), {
      coins,
      count,
      curDayStrek,
      curWeekStreak,
      maxDayStreak,
      maxWeekStreak,
      badges,
    });

    const hasMilestone =
      achievements?.newStreak ||
      achievements?.newWeekStreak ||
      achievements?.newBadge;

    if (hasMilestone) {
      setShowPopup(true);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  async function persistHelpful(helpful) {
    try {
      await updateWellnessHelpful(wellnessId, helpful);
      await fetchAllData();
      queryClient.invalidateQueries({ queryKey: ["wellness", "me"] });
    } catch (err) {
    } finally {
      setIsSubmitting(false);
    }
  }

  function submitHelpful(helpful) {
    if (isSubmitting) {
      return;
    }
    setIsSubmitting(true);

    navigate("/studentdashboard", { state: { refreshingData: true } });
    void persistHelpful(helpful);
  }

  function submitHelpfulInBackground(helpful) {
    if (isSubmitting) {
      return;
    }
    setIsSubmitting(true);
    void persistHelpful(helpful);
  }

  return {
    coinsEarned: achievements?.coinsEarned ?? 0,
    coinTotal: achievements?.coins ?? 0,
    newStreak: achievements?.newStreak ?? null,
    newWeekStreak: achievements?.newWeekStreak ?? null,
    newBadge: achievements?.newBadge ?? null,
    showPopup,
    setShowPopup,
    isSubmitting,
    submitHelpful,
    submitHelpfulInBackground,
  };
}
